import React from 'react'
import { AlertCircle, Save, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useDirtyForm } from '@/contexts/DirtyFormContext'
import { useToast } from '@/contexts/ToastContext'

// sticky banner shown at the top of any page wrapped in DirtyFormProvider.
// drives Save/Discard for the page-scoped batched edit flow. no router
// imports, in-app nav is not blocked (drafts unmount silently with the
// page, see DirtyFormContext.tsx for rationale). browser close / reload
// is intercepted via beforeunload inside the provider.
//
// discard is one-click, no confirmation modal. user reads
// "X unsaved changes" before clicking, so intent is unambiguous.
export const DirtyChangesBar: React.FC = () => {
  const dirty = useDirtyForm()
  const { success, error } = useToast()

  if (!dirty.isUnderDirtyForm || !dirty.hasDirty) return null

  const onSave = async () => {
    const r = await dirty.saveAll()
    if (r.success) success('Saved', `${dirty.count} change${dirty.count === 1 ? '' : 's'} committed.`)
    else error('Save failed', r.error ?? 'Please retry.')
  }

  return (
    <div className="sticky top-0 z-40 -mx-4 sm:-mx-6 lg:-mx-8 mb-4 px-4 sm:px-6 lg:px-8 py-2 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="font-medium">
            {dirty.count} unsaved change{dirty.count === 1 ? '' : 's'}
          </span>
          {dirty.saveError && (
            <span className="text-xs text-red-600 dark:text-red-300 ml-2">— {dirty.saveError}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={dirty.discardAll}
            disabled={dirty.isSaving}
          >
            <X className="h-3.5 w-3.5 mr-1.5" />
            Discard
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={onSave}
            disabled={dirty.isSaving}
            loading={dirty.isSaving}
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            Save
          </Button>
        </div>
      </div>
    </div>
  )
}
